import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRight, MapPin, Plus, RefreshCw, Search, Target } from 'lucide-react';

import { getMyLeadSummary, getMyLeads } from '@/app/services/leadService';
import { MobileHeader } from '@/components/layout/MobileHeader';
import { MobileBadge } from '@/components/ui/MobileBadge';
import { MobileEmptyState } from '@/components/ui/MobileEmptyState';
import { MobileSkeleton } from '@/components/ui/MobileSkeleton';
import { cn } from '@/lib/utils';
import { LEAD_STATUS_LABELS, LEAD_TYPE_LABELS, type FollowUpBucket } from '@/app/types/lead';
import { formatFollowUp, statusVariant } from './leadUi';

type Segment = 'today' | 'overdue' | 'all';

const SEGMENTS: { key: Segment; label: string }[] = [
    { key: 'today', label: 'Due today' },
    { key: 'overdue', label: 'Overdue' },
    { key: 'all', label: 'All' },
];

export default function LeadsPage() {
    const navigate = useNavigate();
    const [segment, setSegment] = useState<Segment>('today');
    const [searchQuery, setSearchQuery] = useState('');

    const { data: summary } = useQuery({
        queryKey: ['lead-summary'],
        queryFn: getMyLeadSummary,
        refetchInterval: 60000,
    });

    const followUp: FollowUpBucket | undefined =
        segment === 'all' ? undefined : (segment as FollowUpBucket);

    const { data, isLoading, refetch, isFetching } = useQuery({
        queryKey: ['leads', segment, searchQuery],
        queryFn: () => getMyLeads({ followUp, search: searchQuery || undefined }),
        refetchInterval: 60000,
    });

    const leads = data?.items ?? [];

    return (
        <div className="flex flex-col min-h-full">
            <MobileHeader
                title="Leads"
                rightAction={
                    <button
                        onClick={() => refetch()}
                        className="flex items-center justify-center w-10 h-10 rounded-full active:bg-accent"
                        disabled={isFetching}
                        aria-label="Refresh leads"
                    >
                        <RefreshCw
                            className={cn('h-5 w-5 text-muted-foreground', isFetching && 'animate-spin')}
                        />
                    </button>
                }
            />

            <div className="flex-1 mobile-scroll-area pb-tab-bar">
                {/* Tapping a stat jumps to that segment — the number and the list agree. */}
                <div className="grid grid-cols-3 gap-2 p-4 pb-2">
                    <StatPill
                        label="Overdue"
                        value={summary?.overdue ?? 0}
                        tone="danger"
                        active={segment === 'overdue'}
                        onClick={() => setSegment('overdue')}
                    />
                    <StatPill
                        label="Due today"
                        value={summary?.due_today ?? 0}
                        tone="warning"
                        active={segment === 'today'}
                        onClick={() => setSegment('today')}
                    />
                    <StatPill
                        label="Open"
                        value={summary?.open_total ?? 0}
                        tone="neutral"
                        active={segment === 'all'}
                        onClick={() => setSegment('all')}
                    />
                </div>

                <div className="px-4 pb-2 flex gap-2">
                    {SEGMENTS.map((s) => (
                        <button
                            key={s.key}
                            onClick={() => setSegment(s.key)}
                            className={cn(
                                'flex-1 py-2 text-sm font-medium rounded-lg transition-colors',
                                segment === s.key
                                    ? 'bg-primary text-primary-foreground'
                                    : 'bg-secondary text-muted-foreground active:bg-accent'
                            )}
                        >
                            {s.label}
                        </button>
                    ))}
                </div>

                <div className="px-4 pb-2">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <input
                            type="text"
                            placeholder="Search name, phone, area..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="mobile-input pl-10"
                        />
                    </div>
                </div>

                {isLoading ? (
                    <LeadSkeleton />
                ) : leads.length === 0 ? (
                    <MobileEmptyState
                        icon={<Target className="h-16 w-16" />}
                        title={emptyTitle(segment, searchQuery)}
                        description={emptyDescription(segment, searchQuery)}
                    />
                ) : (
                    <div className="p-4 pt-2 space-y-3">
                        {leads.map((lead) => {
                            const followUpInfo = formatFollowUp(lead.next_follow_up_date);
                            return (
                                <Link key={lead.id} to={`/leads/${lead.id}`} className="block">
                                    <div className="mobile-card active:bg-accent transition-colors">
                                        <div className="flex items-center justify-between gap-3">
                                            <div className="flex items-center gap-3 flex-1 min-w-0">
                                                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                                    <span className="text-lg font-semibold text-primary">
                                                        {lead.business_name.charAt(0).toUpperCase()}
                                                    </span>
                                                </div>

                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <p className="font-semibold text-foreground truncate">
                                                            {lead.business_name}
                                                        </p>
                                                        {lead.geo_location && (
                                                            <MapPin className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                                                        )}
                                                    </div>

                                                    <p className="text-sm text-muted-foreground truncate">
                                                        {[
                                                            lead.area,
                                                            lead.lead_type
                                                                ? LEAD_TYPE_LABELS[lead.lead_type]
                                                                : null,
                                                        ]
                                                            .filter(Boolean)
                                                            .join(' · ') || lead.phone}
                                                    </p>

                                                    <div className="flex items-center gap-2 mt-1.5">
                                                        <MobileBadge variant={statusVariant[lead.status]}>
                                                            {LEAD_STATUS_LABELS[lead.status]}
                                                        </MobileBadge>
                                                        <span
                                                            className={cn(
                                                                'text-xs font-medium',
                                                                followUpInfo.overdue
                                                                    ? 'text-destructive'
                                                                    : 'text-muted-foreground'
                                                            )}
                                                        >
                                                            {followUpInfo.label}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                                        </div>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                )}
            </div>

            <button
                onClick={() => navigate('/leads/new')}
                className="fixed right-5 bottom-24 z-20 h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center active:scale-95 transition-transform"
                aria-label="Add lead"
            >
                <Plus className="h-6 w-6" />
            </button>
        </div>
    );
}

function emptyTitle(segment: Segment, search: string) {
    if (search) return 'No matches';
    if (segment === 'today') return 'Nothing due today';
    if (segment === 'overdue') return 'Nothing overdue';
    return 'No leads yet';
}

function emptyDescription(segment: Segment, search: string) {
    if (search) return 'No leads match your search';
    if (segment === 'today') return 'No follow-ups scheduled for today';
    if (segment === 'overdue') return "You're all caught up";
    return 'Tap + to add the first shop you visit';
}

function StatPill({
    label,
    value,
    tone,
    active,
    onClick,
}: {
    label: string;
    value: number;
    tone: 'danger' | 'warning' | 'neutral';
    active: boolean;
    onClick: () => void;
}) {
    const toneClasses = {
        danger: 'text-destructive',
        warning: 'text-yellow-400',
        neutral: 'text-foreground',
    };

    return (
        <button
            onClick={onClick}
            className={cn(
                'mobile-card !p-3 text-center transition-colors',
                active ? 'ring-1 ring-primary' : 'active:bg-accent'
            )}
        >
            <p className={cn('text-2xl font-bold', toneClasses[tone])}>{value}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
        </button>
    );
}

function LeadSkeleton() {
    return (
        <div className="p-4 pt-2 space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="mobile-card">
                    <div className="flex items-center gap-3">
                        <MobileSkeleton className="w-12 h-12 rounded-full" />
                        <div className="flex-1 space-y-2">
                            <MobileSkeleton className="h-5 w-40" />
                            <MobileSkeleton className="h-4 w-28" />
                            <MobileSkeleton className="h-4 w-20" />
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}
